import {useMutation, useInfiniteQuery, useQueryClient} from '@tanstack/react-query';
import {deleteRecord, fetchRecords} from '../services/MeteringRecordsService';
import {useState} from 'react';

export const useMeteringRecords = () => {
    const queryClient = useQueryClient();
    const [keyword, setKeyword] = useState('');
    const [limit] = useState(10);

    const sort = {column: 'created', direction: 'desc' as const};

    const meteringRecordsQuery = useInfiniteQuery({
        queryKey: ['metering-records', keyword, limit, sort.column, sort.direction],
        queryFn: ({pageParam}: { pageParam: string | null }) =>
            fetchRecords({limit, sort, nextToken: pageParam}),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ['metering-records']});
        }
    });

    const fetchRecordsAction = () => {
        if (meteringRecordsQuery.hasNextPage) {
            meteringRecordsQuery.fetchNextPage();
        }
    };

    const deleteRecords = (ids: string[]) => {
        if (ids.length > 0) {
            deleteMutation.mutate(ids);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems = meteringRecordsQuery.data?.pages?.flatMap(page =>
        (page && 'items' in page) ? page.items : []
    ) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: meteringRecordsQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: meteringRecordsQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        deleteRecords,
        handleFilterByKeyword,
        deleteMutation
    };
};
